import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Button, Table, FormGroup, Label, Input } from 'reactstrap';
import api from '../../services/api';
import NewTeeChoiceModal from './NewTeeChoiceModal';
import EditTeeChoiceModal from './EditTeeChoiceModal';

const LeagueTeeChoices = ({ league }) => {
    const [teeChoices, setTeeChoices] = useState([]);
    const [newModal, setNewModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [defaultTee, setDefaultTee] = useState('');

    const toggleNewModal = () => setNewModal(!newModal);
    const toggleEditModal = () => setEditModal(!editModal);

    const fetchTeeChoices = useCallback(async () => {
        try {
            const response = await api.get(`/league-tee-choices/?league=${league.id}`);
            // Sort by sort order
            const sorted = response.data.sort((a, b) => a.sort - b.sort);
            setTeeChoices(sorted);
        } catch (error) {
            console.error('Error fetching tee choices:', error);
        }
    }, [league.id]);

    const fetchDefaultTee = useCallback(async () => {
        try {
            const response = await api.get(`/leagues/${league.id}/`);
            setDefaultTee(response.data.default_tee || '');
        } catch (error) {
            console.error('Error fetching default tee:', error);
        }
    }, [league.id]);

    useEffect(() => {
        if (league?.id) {
            fetchTeeChoices();
            fetchDefaultTee();
        }
    }, [league?.id, fetchTeeChoices, fetchDefaultTee]);

    const handleAddTeeChoice = async (teeData) => {
        try {
            await api.post('/league-tee-choices/', {
                league: league.id,
                ...teeData
            });
            fetchTeeChoices();
            toggleNewModal();
        } catch (error) {
            console.error('Error adding tee choice:', error);
            alert('Error adding tee choice: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleEditTeeChoice = async (teeData) => {
        try {
            await api.patch(`/league-tee-choices/${editItem.id}/`, teeData);
            fetchTeeChoices();
            toggleEditModal();
            setEditItem(null);
        } catch (error) {
            console.error('Error updating tee choice:', error);
            alert('Error updating tee choice: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleDeleteTeeChoice = async (teeId) => {
        if (!window.confirm('Are you sure you want to remove this tee choice?')) {
            return;
        }
        try {
            await api.delete(`/league-tee-choices/${teeId}/`);
            fetchTeeChoices();
        } catch (error) {
            console.error('Error deleting tee choice:', error);
            alert('Error deleting tee choice: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleEditClick = (item) => {
        setEditItem(item);
        toggleEditModal();
    };

    const handleUpdateDefaultTee = async () => {
        try {
            await api.patch(`/leagues/${league.id}/`, { default_tee: defaultTee });
            fetchDefaultTee(); // Refresh to confirm the save
        } catch (error) {
            console.error('Error updating default tee:', error);
        }
    };

    if (!league?.id) {
        return <p className="text-muted small">Select a league to manage its tee choices.</p>;
    }

    return (
        <>
            <Card className="mt-3">
                <CardBody className="pt-3">
                    <FormGroup>
                        <Label for="defaultTee">Default Tee</Label>
                        <div className="d-flex gap-2">
                            <Input
                                type="text"
                                id="defaultTee"
                                name="defaultTee"
                                value={defaultTee}
                                onChange={(e) => setDefaultTee(e.target.value)}
                                placeholder="e.g., Blue"
                                style={{ maxWidth: '300px' }}
                            />
                            <Button color="primary" size="sm" onClick={handleUpdateDefaultTee}>
                                Update Default Tee
                            </Button>
                        </div>
                        <small className="text-muted">The default tee selection for team registration</small>
                    </FormGroup>

                    <hr className="my-3" />

                    {teeChoices.length === 0 ? (
                        <p className="text-muted small">No tee choices defined for this league.</p>
                    ) : (
                        <Table bordered hover size="sm" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '60px' }}>Sort</th>
                                    <th>Prompt</th>
                                    <th>Tee Value</th>
                                    <th style={{ width: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teeChoices.map((choice) => (
                                    <tr key={choice.id}>
                                        <td className="text-center">{choice.sort}</td>
                                        <td>{choice.prompt}</td>
                                        <td>{choice.tee}</td>
                                        <td>
                                            <Button 
                                                color="primary" 
                                                size="sm" 
                                                onClick={() => handleEditClick(choice)}
                                            >
                                                Edit
                                            </Button>{' '}
                                            <Button 
                                                color="danger" 
                                                size="sm" 
                                                onClick={() => handleDeleteTeeChoice(choice.id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <div className="mt-3">
                        <Button color="success" size="sm" onClick={toggleNewModal}>
                            + Add Tee Choice
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <NewTeeChoiceModal
                isOpen={newModal}
                toggle={toggleNewModal}
                onSave={handleAddTeeChoice}
            />

            {editItem && (
                <EditTeeChoiceModal
                    isOpen={editModal}
                    toggle={toggleEditModal}
                    onSave={handleEditTeeChoice}
                    item={editItem}
                />
            )}
        </>
    );
};

export default LeagueTeeChoices;
